/*
 * Datazoom, Inc ("COMPANY") CONFIDENTIAL
 * Copyright (c) 2017-2018 [Datazoom, Inc.], All Rights Reserved.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 */

import {
  call,
  put,
  takeLatest,
  all,
  takeEvery,
  take,
  takeLeading,
  select,
} from "redux-saga/effects";
import * as companyAction from "./actions";
import * as authAction from "../auth/actions";

import {
  FetchCompanies,
  FetchACompany,
  CreateCompany,
  UpdateCompany,
  EnableCompany,
  DisableCompany,
  DeleteCompany,
  FetchAllCompanies,
  StarCompany,
  SwitchCompany,
  FetchACompanyLogs,
} from "./services";
import { getauth } from "../auth/selectors";
import { SUPER_ADMIN_ROLE } from "../../app/Constants/Constants";

const delay = (ms) => new Promise((res) => setTimeout(res, ms));
// worker saga: makes the api call when watcher saga sees the action
export function* fetchCompanySaga(action) {
  try {
    const response = yield call(FetchCompanies, action.payload);
    const companies =
      response && response.data && response.data.data ? response.data.data : {};
    companies.list = companies.result;
    delete companies.result;
    yield put(companyAction.FetchCompaniesSuccess(companies));
  } catch (error) {
    // dispatch a failure action to the store with the error
    yield put(companyAction.FetchCompaniesFailed(error));
  }
}
export function* fetchAllCompanySaga(action) {
  /* istanbul ignore next */
  try {
    const response = yield call(FetchAllCompanies, action.payload);
    const companies =
      response && response.data && response.data.data ? response.data.data : {};
    const list = companies.result;
    yield put(companyAction.FetchAllCompaniesSuccess(list));
  } catch (error) {
    // dispatch a failure action to the store with the error
    yield put(companyAction.FetchAllCompaniesFailed(error));
  }
}
export function* fetchACompanySaga(action) {
  /* istanbul ignore next */
  try {
    const response = yield call(FetchACompany, action.payload);
    /* istanbul ignore if */
    if (response && response.data) {
      yield put(companyAction.FetchACompanySuccess(response.data));
    } else {
      yield put(
        companyAction.FetchACompanyFailed({
          message: "Something went wrong. Please try later",
        }),
      );
    }
  } catch (error) {
    // dispatch a failure action to the store with the error
    yield put(companyAction.FetchACompanyFailed(error));
  }
}
export function* createCompanySaga(action) {
  /* istanbul ignore next */
  try {
    const response = yield call(CreateCompany, action.payload);
    if (response && response.data && response.data.success) {
      const successResponse = response && response.data;
      yield put(companyAction.CreateCompanySuccess(successResponse));
    } else {
      const message =
        response && response.data
          ? response.data
          : "Something went wrong. Please try later";
      yield put(companyAction.CreateCompanyFailed(message));
    }
  } catch (error) {
    // dispatch a failure action to the store with the error
    yield put(companyAction.CreateCompanyFailed(error));
  }
}
export function* updateCompanySaga(action) {
  try {
    const response = yield call(UpdateCompany, action.payload);
    /* istanbul ignore else */
    if (response && response.data && response.data.success) {
      const successResponse = response && response.data;
      yield put(companyAction.UpdateCompanySuccess(successResponse));
    } else {
      const message =
        response && response.data
          ? response.data
          : "Something went wrong. Please try later";
      yield put(companyAction.UpdateCompanyFailed(message));
    }
  } catch (error) {
    // dispatch a failure action to the store with the error
    yield put(companyAction.UpdateCompanyFailed(error));
  }
}
export function* enableCompanySaga(action) {
  try {
    const response = yield call(EnableCompany, action.payload);
    if (response === true) {
      yield put(companyAction.EnableCompanySuccess("success"));
    } else {
      yield put(
        companyAction.EnableCompanyFailed({
          message: "Something went wrong. Please try later",
        }),
      );
    }
  } catch (error) {
    // dispatch a failure action to the store with the error
    yield put(companyAction.EnableCompanyFailed(error));
  }
}
export function* disableCompanySaga(action) {
  try {
    const response = yield call(DisableCompany, action.payload);
    if (response === true) {
      yield put(companyAction.DisableCompanySuccess("success"));
    } else {
      yield put(
        companyAction.DisableCompanyFailed({
          message: "Something went wrong. Please try later",
        }),
      );
    }
  } catch (error) {
    // dispatch a failure action to the store with the error
    yield put(companyAction.DisableCompanyFailed(error));
  }
}
export function* deleteCompanySaga(action) {
  try {
    const response = yield call(DeleteCompany, action.payload);
    /* istanbul ignore else */
    if (response) {
      yield put(companyAction.DeleteCompanySuccess("success"));
    } else {
      yield put(
        companyAction.DeleteCompanyFailed({
          message: "Something went wrong. Please try later",
        }),
      );
    }
  } catch (error) {
    // dispatch a failure action to the store with the error
    yield put(companyAction.DeleteCompanyFailed(error));
  }
}

export function* starCompanySaga(action) {
  try {
    const response = yield call(
      StarCompany,
      action.payload.companyuuid,
      "POST",
    );
    /* istanbul ignore else */
    if (response && response.data && response.data.success) {
      yield put(companyAction.StarCompanySuccess(action.payload));
    } else {
      yield put(
        companyAction.StarCompanyFailed({
          message: "Something went wrong. Please try later",
        }),
      );
    }
  } catch (error) {
    // dispatch a failure action to the store with the error
    yield put(companyAction.StarCompanyFailed(error));
  }
}

export function* unStarCompanySaga(action) {
  try {
    const response = yield call(StarCompany, action.payload, "DELETE");
    /* istanbul ignore else */
    if (response && response.data && response.data.success) {
      yield put(companyAction.UnStarCompanySuccess(action.payload));
    } else {
      yield put(
        companyAction.UnStarCompanyFailed({
          message: "Something went wrong. Please try later",
        }),
      );
    }
  } catch (error) {
    // dispatch a failure action to the store with the error
    yield put(companyAction.UnStarCompanyFailed(error));
  }
}

export function* switchCompanySaga(action) {
  try {
    const response = yield call(SwitchCompany, action.payload);
    /* istanbul ignore else */
    if (response?.data?.success) {
      yield put(companyAction.SwitchCompanySuccess(response.data));
      const auth = yield select(getauth);
      if (auth.user.roleName !== SUPER_ADMIN_ROLE) {
        yield put(authAction.UpdateAuthRole(response.data.data?.role));
      }
    } else {
      const message = response?.data
        ? response.data
        : "Something went wrong. Please try later";
      yield put(companyAction.SwitchCompanyFailed(message));
    }
  } catch (error) {
    // dispatch a failure action to the store with the error
    yield put(companyAction.SwitchCompanyFailed(error));
  }
}
export function* fetchACompanyLogsSaga(action) {
  try {
    const response = yield call(FetchACompanyLogs, action.payload);
    if (response && response.data && response.data.success) {
      yield put(companyAction.FetchACompanyLogsSuccess(response.data));
    } else {
      yield put(companyAction.FetchACompanyLogsFailed(response.data));
    }
  } catch (error) {
    // dispatch a failure action to the store with the error
    yield put(companyAction.FetchACompanyLogsFailed(error));
  }
}
// watcher saga: watches for actions dispatched to the store, starts worker saga
export function* companyWatcher() {
  yield all([
    takeLatest(companyAction.types.FETCH_COMPANIES, fetchCompanySaga),
    takeLatest(companyAction.types.FETCH_ALL_COMPANIES, fetchAllCompanySaga),
    takeLatest(companyAction.types.FETCH_A_COMPANY, fetchACompanySaga),
    takeLatest(companyAction.types.CREATE_COMPANY, createCompanySaga),
    takeLatest(companyAction.types.UPDATE_COMPANY, updateCompanySaga),
    takeLatest(companyAction.types.ENABLE_COMPANY, enableCompanySaga),
    takeLatest(companyAction.types.DISABLE_COMPANY, disableCompanySaga),
    takeLatest(companyAction.types.DELETE_COMPANY, deleteCompanySaga),
    takeLeading(companyAction.types.STAR_COMPANY, starCompanySaga),
    takeLeading(companyAction.types.UN_STAR_COMPANY, unStarCompanySaga),
    takeLeading(companyAction.types.SWITCH_COMAPANY, switchCompanySaga),
    takeLatest(companyAction.types.FETCH_A_COMPANY_LOGS, fetchACompanyLogsSaga),
  ]);
}
